import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Play, Check, GitCompare, ChevronUp, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getVisuals } from "./treemap-utils";

const STATUS_CONFIG = {
  pending: { label: null, cls: "" },
  in_progress: { label: "In Progress", cls: "border-t-2 border-t-amber-400/50" },
  completed: { label: "Completed", cls: "opacity-50" },
};

function ActionCard({ node, index, status, onStatusChange, onFocus, onCompare }) {
  const v = getVisuals(node.state, node.urgency);
  const bd = node.biggest_driver;
  const isCompleted = status === "completed";
  const isInProgress = status === "in_progress";

  const impactLevel = (node.impact_potential || "").toLowerCase().startsWith("high")
    ? "High" : (node.impact_potential || "").toLowerCase().startsWith("low") ? "Low" : "Medium";

  const urgencyLabel = node.urgency >= 3 ? "Urgent" : node.urgency >= 2 ? "Attention" : "Stable";
  const urgencyColor = node.urgency >= 3 ? "text-red-400 bg-red-500/10 border-red-500/20"
    : node.urgency >= 2 ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
    : "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ delay: index * 0.06, type: "spring", stiffness: 300, damping: 28 }}
      className={`shrink-0 w-[280px] rounded-xl border border-white/[0.06] p-3.5 cursor-pointer transition-all duration-300 hover:border-white/[0.12] ${STATUS_CONFIG[status]?.cls || ""}`}
      style={{ background: isCompleted ? "rgba(255,255,255,0.01)" : "rgba(255,255,255,0.02)" }}
      onClick={() => onFocus(node)}
      data-testid={`priority-card-${node.id}`}
    >
      {/* Number + Category */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
            style={{ background: `${v.accent}15`, color: v.accent }}>
            {index + 1}
          </span>
          <span className={`text-xs font-semibold uppercase tracking-wider ${isCompleted ? "line-through text-white/25" : "text-white/60"}`}>
            {node.title}
          </span>
        </div>
        {STATUS_CONFIG[status]?.label && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isInProgress ? "bg-amber-500/10 text-amber-400/70" : "bg-emerald-500/10 text-emerald-400/70"}`}>
            {STATUS_CONFIG[status].label}
          </span>
        )}
      </div>

      {/* Suggested Action */}
      <p className={`text-[12px] leading-relaxed mb-2 ${isCompleted ? "line-through text-white/20" : "text-white/70"}`}>
        {node.suggested_action}
      </p>

      {/* Tags */}
      <div className="flex items-center gap-1.5 mb-2">
        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 h-5 border ${urgencyColor}`}>
          {urgencyLabel}
        </Badge>
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-5 border-white/10 text-white/40">
          {impactLevel} Impact
        </Badge>
      </div>

      {/* Driver */}
      {bd && (
        <p className="text-[10px] text-white/25 leading-snug">
          Driven by {bd.source.toLowerCase()} ({bd.percentage}%)
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/[0.04]">
        {status !== "in_progress" && status !== "completed" && (
          <button onClick={(e) => { e.stopPropagation(); onStatusChange(node.id, "in_progress"); }}
            className="flex items-center gap-1 text-[10px] text-amber-400/50 hover:text-amber-400/80 transition-colors px-1.5 py-0.5 rounded hover:bg-amber-500/5"
            data-testid={`mark-progress-${node.id}`}>
            <Play className="w-2.5 h-2.5" /> In Progress
          </button>
        )}
        {status === "in_progress" && (
          <button onClick={(e) => { e.stopPropagation(); onStatusChange(node.id, "completed"); }}
            className="flex items-center gap-1 text-[10px] text-emerald-400/50 hover:text-emerald-400/80 transition-colors px-1.5 py-0.5 rounded hover:bg-emerald-500/5"
            data-testid={`mark-complete-${node.id}`}>
            <Check className="w-2.5 h-2.5" /> Complete
          </button>
        )}
        {status === "completed" && (
          <button onClick={(e) => { e.stopPropagation(); onStatusChange(node.id, "pending"); }}
            className="flex items-center gap-1 text-[10px] text-white/25 hover:text-white/50 transition-colors px-1.5 py-0.5 rounded hover:bg-white/5"
            data-testid={`mark-reopen-${node.id}`}>
            Reopen
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onCompare(node); }}
          className="flex items-center gap-1 text-[10px] text-white/25 hover:text-white/50 transition-colors px-1.5 py-0.5 rounded hover:bg-white/5 ml-auto"
          data-testid={`compare-alt-${node.id}`}>
          <GitCompare className="w-2.5 h-2.5" /> Compare
        </button>
      </div>
    </motion.div>
  );
}

export function PriorityQueue({ nodes, actionStatuses, onStatusChange, onFocusTile, onCompareWith, open, onToggle }) {
  const topActions = useMemo(() => {
    if (!nodes?.length) return [];
    return [...nodes]
      .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
  }, [nodes]);

  return (
    <div className="relative" data-testid="priority-queue-wrapper">
      {/* Toggle bar */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-center gap-2 py-1.5 border-t border-white/[0.06] text-white/40 hover:text-white/60 transition-colors"
        style={{ background: "rgba(2,4,2,0.9)" }}
        data-testid="priority-queue-toggle"
      >
        <Zap className="w-3 h-3 text-primary/60" />
        <span className="text-[10px] font-semibold uppercase tracking-wider">Top Actions</span>
        <span className="text-[9px] text-white/20">Based on impact & urgency</span>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
      </button>

      {/* Cards pane */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="overflow-hidden border-t border-white/[0.04]"
            style={{ background: "rgba(2,4,2,0.92)" }}
            data-testid="priority-queue-sidebar"
          >
            <div className="p-3 flex gap-3 overflow-x-auto">
              <AnimatePresence mode="popLayout">
                {topActions.map((node, i) => (
                  <ActionCard
                    key={node.id}
                    node={node}
                    index={i}
                    status={actionStatuses[node.id] || "pending"}
                    onStatusChange={onStatusChange}
                    onFocus={onFocusTile}
                    onCompare={onCompareWith}
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
